"use client";

import { Translate } from "@/components/Translate";
import { ChevronRight } from "@/components/icons/Chevron";
import { useEmblaNavigation } from "@/hooks/useEmblaNavigation";
import { Link } from "@/i18n/Link";
import { useTranslations } from "@/i18n/client";
import type { LinkReturn } from "@frend-digital/storyblok";
import { clsx } from "@frend-digital/ui";
import { useEmblaCarousel } from "@frend-digital/ui/embla";
import type { EmblaCarouselType } from "embla-carousel";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { type ComponentProps, type RefObject, useImperativeHandle } from "react";
import styles from "./Carousel.module.css";

export interface CarouselProps
	extends Omit<ComponentProps<"div">, "className" | "link" | "ref" | "title"> {
	children: React.ReactNode;
	className?: string;
	containerClassName?: string;
	ref?: RefObject<EmblaCarouselType | undefined>;
	showControls?: boolean;
	activeScroll?: boolean;
	title?: string;
	link?: LinkReturn;
	linkText?: string;
	wrapperClassName?: string;
	titleStyle?: string;
	onLinkClick?: () => void;
}

export const Carousel = ({
	children,
	className,
	containerClassName,
	ref: refProp,
	showControls,
	activeScroll,
	title,
	link,
	linkText,
	wrapperClassName,
	titleStyle,
	onLinkClick,
	...rest
}: CarouselProps) => {
	const t = useTranslations();
	const [ref, emblaAPI] = useEmblaCarousel({
		loop: false,
		align: "start",
		dragFree: activeScroll,
	});

	useImperativeHandle(refProp, () => emblaAPI.current);
	const { nextDisabled, onNextClick, onPrevClick, prevDisabled } = useEmblaNavigation(emblaAPI);
	return (
		<div className={clsx(styles.root, wrapperClassName)} {...rest}>
			{(title || (linkText && link) || showControls) && (
				<div className={clsx(styles.header, titleStyle)}>
					<div className={styles.headerInner}>
						{title && (
							<h2 className={styles.title}>
								<Translate>{title}</Translate>
							</h2>
						)}
						{linkText && link && (
							<Link href={link?.href} className={styles.link} onClick={onLinkClick}>
								{linkText}
								<span className={styles.chevron}>
									<ChevronRight height={12} width={12} color="var(--white)" />
								</span>
							</Link>
						)}
					</div>
					{showControls && (
						<div className={styles.controls}>
							<div className={styles.buttons}>
								<button
									type="button"
									aria-label={t("Previous")}
									onClick={onPrevClick}
									disabled={prevDisabled}
								>
									<ChevronRight className={styles.prev} />
								</button>
								<button
									type="button"
									aria-label={t("Next")}
									onClick={onNextClick}
									disabled={nextDisabled}
								>
									<ChevronRight />
								</button>
							</div>
						</div>
					)}
				</div>
			)}
			<div className={clsx(styles.sliderRoot, className)} ref={ref}>
				<div className={clsx(styles.container, containerClassName)}>{children}</div>
			</div>
		</div>
	);
};
